'use client';

import { useEffect, useState } from 'react';

type ScrollSpyOptions = {
  offset?: number;
  enabled?: boolean;
  root?: HTMLElement | null;
};

const DEFAULT_OFFSET = 112;

function getMarkerTop(element: HTMLElement, root: HTMLElement | null): number {
  if (!root) {
    return element.getBoundingClientRect().top;
  }
  return element.getBoundingClientRect().top - root.getBoundingClientRect().top;
}

export function useScrollSpy(sectionIds: string[], options: ScrollSpyOptions = {}) {
  const { offset = DEFAULT_OFFSET, enabled = true, root = null } = options;
  const [activeId, setActiveId] = useState(sectionIds[0] ?? 'introduction');

  useEffect(() => {
    if (!enabled || sectionIds.length === 0) return;

    let frame = 0;
    let resizeObserver: ResizeObserver | undefined;
    const scrollTarget: HTMLElement | Window = root ?? window;

    const update = () => {
      const markers = sectionIds
        .map((id) => {
          const element = document.getElementById(id);
          return element ? { id, top: getMarkerTop(element, root) } : null;
        })
        .filter((marker): marker is { id: string; top: number } => marker !== null);

      if (markers.length === 0) return;

      let next = markers[0]?.id ?? sectionIds[0] ?? 'introduction';

      for (const marker of markers) {
        if (marker.top <= offset) {
          next = marker.id;
        }
      }

      setActiveId((current) => (current === next ? current : next));
    };

    const onScroll = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    const waitForDom = () => {
      const found = sectionIds.some((id) => document.getElementById(id));
      if (!found) {
        frame = window.requestAnimationFrame(waitForDom);
        return;
      }
      update();
    };

    waitForDom();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    if (root && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onScroll);
      resizeObserver.observe(root);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      scrollTarget.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      resizeObserver?.disconnect();
    };
  }, [enabled, offset, root, sectionIds.join('|')]);

  return activeId;
}

export function useDocumentScrollProgress(enabled = true, root: HTMLElement | null = null): number {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    let frame = 0;
    let resizeObserver: ResizeObserver | undefined;
    const scrollTarget: HTMLElement | Window = root ?? window;

    const update = () => {
      let next = 0;

      if (root) {
        const maxScroll = root.scrollHeight - root.clientHeight;
        next = maxScroll > 0 ? Math.min(1, Math.max(0, root.scrollTop / maxScroll)) : 0;
      } else {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        next = maxScroll > 0 ? Math.min(1, Math.max(0, window.scrollY / maxScroll)) : 0;
      }

      setProgress((current) => (Math.abs(current - next) < 0.001 ? current : next));
    };

    const onScroll = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    update();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    if (root && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(root);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      scrollTarget.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      resizeObserver?.disconnect();
    };
  }, [enabled, root]);

  return progress;
}
